import { CalendarDays, Pill, FlaskConical, ScanLine, HeartHandshake, ShieldCheck, ShieldOff, CheckCircle2, XCircle } from 'lucide-react';
import { formatTime } from '../../utils/clock';

// Shared vocabulary for the Mitra One Care section. Everything here is MODULE
// level on purpose: a component declared inside another component is a new type
// on every render, which remounts it and loses input focus after one keystroke —
// a bug this codebase has re-introduced enough times to be worth the note.

// The five activity kinds the API returns, in the order the filter chips show
// them. Keyed by the exact `kind` string so a row can be decorated without a
// switch at every call site.
export const ACTIVITY_KINDS = [
    { id: 'appointment', label: 'Appointments', singular: 'Appointment', icon: CalendarDays, tone: 'text-[#2D8A8A] bg-[#4ABDBD]/10' },
    { id: 'pharmacy', label: 'Pharmacy', singular: 'Pharmacy dispense', icon: Pill, tone: 'text-emerald-700 bg-emerald-50' },
    { id: 'lab', label: 'Lab', singular: 'Lab order', icon: FlaskConical, tone: 'text-indigo-700 bg-indigo-50' },
    { id: 'radiology', label: 'Radiology', singular: 'Imaging study', icon: ScanLine, tone: 'text-purple-700 bg-purple-50' },
    { id: 'care_visit', label: 'Home care', singular: 'Home care visit', icon: HeartHandshake, tone: 'text-[#D7A05C] bg-[#D7A05C]/10' },
];

export const KIND_MAP = Object.fromEntries(ACTIVITY_KINDS.map((k) => [k.id, k]));

// Dates arrive as ISO strings, but a few timeline rows legitimately carry null
// (a record with no usable date sorts last rather than to 1970 server-side), so
// every formatter here has to survive being handed nothing.
export const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB');
};

export const formatDateTime = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    // Time of day is 12-hour, the one clock format (utils/clock.js).
    return `${d.toLocaleDateString('en-GB')} ${formatTime(d)}`;
};

// Break-glass details are not a fixed schema — the API shapes them per kind and
// adds fields over time — so keys are prettified rather than mapped by hand.
export const prettyKey = (key) =>
    String(key)
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/^./, (c) => c.toUpperCase());

// System identifiers (appointmentId, orderId, dispenseItemId, id, _id, …) are
// never drawn on screen (owner rule, 29 Sep 2026). The break-glass detail is
// rendered structurally, so its id-shaped keys are skipped by name. A document
// NUMBER (reportNo, invoiceNo, batchNo) does not end in "Id" and still shows.
export const isSystemIdKey = (key) => /^_?id$|Ids?$/.test(String(key));

const ISO_DATE = /^\d{4}-\d{2}-\d{2}([T ]|$)/;

export const formatScalar = (value) => {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'string' && ISO_DATE.test(value)) return formatDateTime(value);
    return String(value);
};

export const isBlank = (value) =>
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0);

export const MocStatusBadge = ({ status }) => (
    <span
        data-testid={`moc-status-${status}`}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'
            }`}
    >
        {status === 'active' ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldOff className="w-3.5 h-3.5" />}
        {status === 'active' ? 'Active' : 'Inactive'}
    </span>
);

// Verification is shown for email and phone separately because they verify
// independently — a login policy may only ever have confirmed one of them.
export const VerifiedMark = ({ verified }) =>
    verified ? (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
            <CheckCircle2 className="w-3.5 h-3.5" /> Verified
        </span>
    ) : (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-400">
            <XCircle className="w-3.5 h-3.5" /> Unverified
        </span>
    );
